// ══════════════════════════════════════════════════════════════
//  Export Utilities — CSV + DXF Export für Raumbuch-Daten
//  Client-seitig, kein Server-Roundtrip.
//
//  DXF: AC1009 (AutoCAD R12) — 2D-Grundrisse, Geschosse
//  nebeneinander angeordnet, nur Wandlinien (keine Flächen).
// ══════════════════════════════════════════════════════════════

import { RAUMBUCH_COLUMNS } from './tableColumns'
import type { RaumbuchRow } from './tableColumns'

// ── CSV Export ──────────────────────────────────────────────

function escapeCSV(val: string): string {
  if (val.includes('"') || val.includes(';') || val.includes('\n') || val.includes('\r')) {
    return `"${val.replace(/"/g, '""')}"`
  }
  return val
}

export function exportCSV(rows: RaumbuchRow[], filename = 'raumbuch.csv'): void {
  const header = RAUMBUCH_COLUMNS.map(c => c.label)
  const csvRows = [header.join(';')]

  for (const row of rows) {
    const vals = RAUMBUCH_COLUMNS.map(c => {
      const v = row.values[c.key]
      return escapeCSV(String(v ?? ''))
    })
    csvRows.push(vals.join(';'))
  }

  const bom = '\uFEFF'
  const blob = new Blob([bom + csvRows.join('\r\n')], { type: 'text/csv;charset=utf-8' })
  downloadBlob(blob, filename)
}

// ── DXF Export (AC1009 / AutoCAD R12) ───────────────────────

interface DXFVolume {
  label: string
  name: string
  floorId: string | null
  profile: { x: number; y: number }[]
  position: { x: number; y: number; z: number }
  scale: { x: number; y: number; z: number }
}

function collectVolumes(rows: RaumbuchRow[]): DXFVolume[] {
  const vols: any[] = (window as any)._clipExtrudedVolumes || []
  const rowIds = new Set(rows.map(r => r.volumeId ?? r.id))

  return vols
    .filter((v: any) => v._volumeType === 'room' && rowIds.has(v.id))
    .map((v: any) => ({
      label: v._raumbuch?.nr || v.label || '',
      name: v.name || '',
      floorId: v._floorId || null,
      profile: [...v.profile],
      position: { x: v.position.x, y: v.position.y, z: v.position.z },
      scale: { x: v.scale.x, y: v.scale.y, z: v.scale.z },
    }))
}

/** Profile point → world XY (ignoring Z) */
function toWorldXY(
  pt: { x: number; y: number },
  pos: { x: number; y: number },
  scale: { x: number; y: number },
): [number, number] {
  return [
    pos.x + pt.x * scale.x,
    pos.y + pt.y * scale.y,
  ]
}

function getFloorLayers(volumes: DXFVolume[]): string[] {
  const set = new Set<string>()
  for (const v of volumes) set.add(v.floorId || 'Unbekannt')
  return [...set].sort((a, b) => {
    const order: Record<string, number> = {
      UG2: 0, UG1: 1, EG: 2, OG1: 3, OG2: 4, OG3: 5, DG: 6, Unbekannt: 99,
    }
    return (order[a] ?? 50) - (order[b] ?? 50)
  })
}

function fmt(n: number): string {
  return n.toFixed(6)
}

const FLOOR_COLORS: Record<string, number> = {
  UG2: 30, UG1: 50, EG: 3, OG1: 5, OG2: 1, OG3: 6, DG: 4, Unbekannt: 8,
}

/** Geschoss-Reihenfolge für Platzierung */
const FLOOR_ORDER: Record<string, number> = {
  UG2: 0, UG1: 1, EG: 2, OG1: 3, OG2: 4, OG3: 5, DG: 6, Unbekannt: 99,
}

export function exportDXF(rows: RaumbuchRow[], filename = 'raumbuch.dxf'): void {
  const volumes = collectVolumes(rows)
  if (volumes.length === 0) {
    alert('Keine Raumvolumen zum Exportieren gefunden.')
    return
  }

  const floors = getFloorLayers(volumes)
  const lines: string[] = []

  function w(code: number, val: number) {
    lines.push(String(code))
    lines.push(fmt(val))
  }
  function wi(code: number, val: number) {
    lines.push(String(code))
    lines.push(String(val))
  }
  function ws(code: number, val: string) {
    lines.push(String(code))
    lines.push(val)
  }

  // ── Bounding Box pro Geschoss berechnen ───────────────────
  // Damit wir die Geschosse mit richtigem Abstand nebeneinander platzieren können
  interface FloorBounds { minX: number; maxX: number; minY: number; maxY: number }
  const floorBounds: Record<string, FloorBounds> = {}

  for (const fl of floors) {
    const floorVols = volumes.filter(v => (v.floorId || 'Unbekannt') === fl)
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity

    for (const vol of floorVols) {
      for (const pt of vol.profile) {
        const [wx, wy] = toWorldXY(pt, vol.position, vol.scale)
        if (wx < minX) minX = wx
        if (wx > maxX) maxX = wx
        if (wy < minY) minY = wy
        if (wy > maxY) maxY = wy
      }
    }
    floorBounds[fl] = { minX, maxX, minY, maxY }
  }

  // ── X-Offset pro Geschoss berechnen (nebeneinander, mit Abstand)
  const FLOOR_GAP = 5.0 // Meter Abstand zwischen Geschossen
  const floorOffsetX: Record<string, number> = {}
  let currentX = 0

  for (const fl of floors) {
    const bounds = floorBounds[fl]
    // Offset so dass minX des Geschosses bei currentX liegt
    floorOffsetX[fl] = currentX - bounds.minX
    const width = bounds.maxX - bounds.minX
    currentX += width + FLOOR_GAP
  }

  // ════════════════════════════════════════════════════════════
  // HEADER
  // ════════════════════════════════════════════════════════════
  ws(0, 'SECTION')
  ws(2, 'HEADER')
  ws(9, '$ACADVER')
  ws(1, 'AC1009')
  ws(9, '$INSUNITS')
  wi(70, 6)
  ws(9, '$MEASUREMENT')
  wi(70, 1)
  ws(0, 'ENDSEC')

  // ════════════════════════════════════════════════════════════
  // TABLES
  // ════════════════════════════════════════════════════════════
  ws(0, 'SECTION')
  ws(2, 'TABLES')

  // LTYPE
  ws(0, 'TABLE')
  ws(2, 'LTYPE')
  wi(70, 1)
  ws(0, 'LTYPE')
  ws(2, 'CONTINUOUS')
  wi(70, 0)
  ws(3, 'Solid line')
  wi(72, 65)
  wi(73, 0)
  w(40, 0.0)
  ws(0, 'ENDTAB')

  // LAYER — ein Layer pro Geschoss + Label-Layer
  ws(0, 'TABLE')
  ws(2, 'LAYER')
  wi(70, 1 + floors.length + 1)

  // Default layer
  ws(0, 'LAYER')
  ws(2, '0')
  wi(70, 0)
  wi(62, 7)
  ws(6, 'CONTINUOUS')

  // Floor layers
  for (const fl of floors) {
    ws(0, 'LAYER')
    ws(2, fl)
    wi(70, 0)
    wi(62, FLOOR_COLORS[fl] || 7)
    ws(6, 'CONTINUOUS')
  }

  // Label layer
  ws(0, 'LAYER')
  ws(2, 'Labels')
  wi(70, 0)
  wi(62, 8) // gray
  ws(6, 'CONTINUOUS')

  ws(0, 'ENDTAB')
  ws(0, 'ENDSEC')

  // ════════════════════════════════════════════════════════════
  // ENTITIES — 2D Grundrisse, Geschosse nebeneinander
  // ════════════════════════════════════════════════════════════
  ws(0, 'SECTION')
  ws(2, 'ENTITIES')

  for (const fl of floors) {
    const floorVols = volumes.filter(v => (v.floorId || 'Unbekannt') === fl)
    const offsetX = floorOffsetX[fl]
    const bounds = floorBounds[fl]

    // ── Geschoss-Label (großer Text oberhalb) ───────────────
    const labelX = offsetX + bounds.minX + (bounds.maxX - bounds.minX) / 2
    const labelY = bounds.maxY + 1.5

    ws(0, 'TEXT')
    ws(8, 'Labels')
    w(10, labelX)
    w(20, labelY)
    w(30, 0.0)
    w(40, 0.8) // Texthöhe 80cm → gut lesbar im Plan
    wi(72, 1) // horizontal center
    wi(73, 0) // baseline
    ws(1, fl)
    // Alignment-Punkt (für zentrierten Text)
    w(11, labelX)
    w(21, labelY)
    w(31, 0.0)

    // ── Räume als geschlossene Polylinien (Wandlinien) ──────
    for (const vol of floorVols) {
      const pts = vol.profile
      if (pts.length < 3) continue

      // Raum-Polyline
      ws(0, 'POLYLINE')
      ws(8, fl)
      wi(66, 1)
      wi(70, 1) // closed

      for (const pt of pts) {
        const [wx, wy] = toWorldXY(pt, vol.position, vol.scale)
        ws(0, 'VERTEX')
        ws(8, fl)
        w(10, wx + offsetX)
        w(20, wy)
        w(30, 0.0)
      }
      ws(0, 'SEQEND')
      ws(8, fl)

      // Raum-Label (klein, zentriert im Raum)
      // Schwerpunkt berechnen
      let cx = 0, cy = 0
      for (const pt of pts) {
        const [wx, wy] = toWorldXY(pt, vol.position, vol.scale)
        cx += wx
        cy += wy
      }
      cx = cx / pts.length + offsetX
      cy = cy / pts.length

      ws(0, 'TEXT')
      ws(8, fl)
      w(10, cx)
      w(20, cy)
      w(30, 0.0)
      w(40, 0.15) // 15cm Texthöhe
      wi(72, 1) // zentriert
      wi(73, 2) // middle
      ws(1, `${vol.label} ${vol.name}`.trim())
      w(11, cx)
      w(21, cy)
      w(31, 0.0)
    }
  }

  ws(0, 'ENDSEC')
  ws(0, 'EOF')

  const content = lines.join('\r\n')
  const blob = new Blob([content], { type: 'application/dxf' })
  downloadBlob(blob, filename)
}

// ── Download helper ─────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
